import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiSearch, FiPackage, FiArrowLeft, FiExternalLink, FiCheckCircle } = FiIcons;

const STATUS_FLOW = [
  { key: 'pending', label: 'Order Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'packing', label: 'Packing' },
  { key: 'ready_to_ship', label: 'Ready to Ship' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'delivered', label: 'Delivered' }
];

function OrderTracking() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [shippingAgent, setShippingAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const trackOrder = async (e) => {
    if (e) e.preventDefault();
    if (!trackingNumber.trim()) return;
    setLoading(true);
    setError('');
    setOrder(null);
    setItems([]);

    const { data: orderData, error: orderError } = await supabase
      .from('orders_1750000001')
      .select('*')
      .eq('tracking_number', trackingNumber.trim().toUpperCase())
      .limit(1)
      .single();

    if (orderError || !orderData) {
      setError('No order found with this tracking number. Please check and try again.');
      setLoading(false);
      setSearched(true);
      return;
    }

    setOrder(orderData);

    const { data: itemsData } = await supabase
      .from('order_items_1750000001')
      .select('*')
      .eq('order_id', orderData.id);
    setItems(itemsData || []);

    const { data: settingsData } = await supabase
      .from('settings_1750000003')
      .select('*')
      .eq('key', 'shipping_agent')
      .limit(1)
      .single();
    if (settingsData) setShippingAgent(settingsData.value);

    setLoading(false);
    setSearched(true);
  };

  const currentStepIndex = order
    ? STATUS_FLOW.findIndex((s) => s.key === order.status)
    : -1;

  return (
    <main className="tracking-page">
      <div className="tracking-header section-shell">
        <Link to="/"><SafeIcon icon={FiArrowLeft} /> Back to Home</Link>
        <h1>Track Your Order</h1>
        <p>Enter your tracking number to see your order status</p>
      </div>

      <div className="tracking-search section-shell">
        <form onSubmit={trackOrder} className="tracking-form">
          <div className="tracking-input-wrap">
            <SafeIcon icon={FiPackage} />
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
              placeholder="Enter tracking number (e.g. DG-XXXXXXXX)"
              autoFocus
            />
          </div>
          <button type="submit" className="button primary gradient-btn" disabled={loading || !trackingNumber.trim()}>
            <SafeIcon icon={FiSearch} />
            {loading ? 'Searching…' : 'Track Order'}
          </button>
        </form>
      </div>

      {error && (
        <div className="tracking-result section-shell">
          <div className="tracking-error">
            <SafeIcon icon={FiSearch} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {order && (
        <div className="tracking-result section-shell">
          <div className="tracking-card">
            <div className="tracking-card-header">
              <div>
                <h2>Order #{order.id.slice(0, 8)}</h2>
                <span className="tracking-number">{order.tracking_number}</span>
              </div>
              <span className={`status-badge ${order.status}`}>
                {STATUS_FLOW.find((s) => s.key === order.status)?.label || order.status}
              </span>
            </div>

            <div className="tracking-progress">
              {STATUS_FLOW.map((step, i) => (
                <div
                  key={step.key}
                  className={`tracking-step ${i <= currentStepIndex ? 'completed' : ''} ${i === currentStepIndex ? 'current' : ''}`}
                >
                  <div className="step-dot">
                    {i <= currentStepIndex ? <SafeIcon icon={FiCheckCircle} /> : <span>{i + 1}</span>}
                  </div>
                  <span className="step-label">{step.label}</span>
                  {i < STATUS_FLOW.length - 1 && <div className="step-line" />}
                </div>
              ))}
            </div>

            <div className="tracking-details">
              <div className="tracking-info-col">
                <h3>Shipping Details</h3>
                <div className="tracking-info-row"><span>Name</span><strong>{order.customer_name}</strong></div>
                <div className="tracking-info-row"><span>Address</span><strong>{order.shipping_address}</strong></div>
                <div className="tracking-info-row"><span>District</span><strong>{order.shipping_district}</strong></div>
                <div className="tracking-info-row"><span>Shipping</span><strong>Rs. {Number(order.shipping_cost || 0).toLocaleString()}</strong></div>
              </div>
              <div className="tracking-info-col">
                <h3>Items ({items.length})</h3>
                {items.map((item) => (
                  <div className="tracking-info-row" key={item.id}>
                    <span>{item.product_name} × {item.quantity}</span>
                    <strong>Rs. {(Number(item.unit_price) * item.quantity).toLocaleString()}</strong>
                  </div>
                ))}
                <div className="tracking-info-row tracking-total">
                  <span>Total</span>
                  <strong>Rs. {Number(order.total_amount).toLocaleString()}</strong>
                </div>
              </div>
            </div>

            {order.status === 'picked_up' && order.external_tracking && shippingAgent && (
              <div className="tracking-external">
                <h3>Package Tracking</h3>
                <a
                  href={`${shippingAgent.trackingUrl}${order.external_tracking}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button secondary"
                >
                  <SafeIcon icon={FiExternalLink} /> Track on {shippingAgent.name}
                </a>
              </div>
            )}

            <div className="tracking-date">
              Placed on {new Date(order.created_at).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default OrderTracking;